import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Tooltip, message, Drawer } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import LanguageSwitcher from '@components/LanguageSwitcher';
import demoPng from '@assets/demo.png';
import { supportImg } from '@utils/utils';
import useSetImg from '@hooks/useSetImg';
import stores from '@stores';
import { safeLoadImageFromUrl } from '@utils/utils';

// 创建一个增强的图标封装组件，支持 lucide-react 和 Font Awesome
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  // 如果传入的是图标名称字符串
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  // 如果传入的是组件
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  // 如果没有有效的图标，则不渲染任何内容
  return null;
};

const LandingPage = ({ onStartEditing }) => {
  const { t } = useTranslation('pages');
  const { t: tCommon } = useTranslation('common');
  const [imageUrl, setImageUrl] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const getFile = useSetImg(stores);
  const [messageApi, contextHolder] = message.useMessage();
  const promoProducts = useMemo(
    () => [
      {
        id: 'ethanYolo',
        icon: '/product-icons/ethanyolo.svg',
        url: 'https://ethanyolo.pixzens.com/',
      },
      {
        id: 'pylonk',
        icon: '/product-icons/pylonk.svg',
        url: 'https://pylonk.pixzens.com/',
      },
      {
        id: 'dataPick',
        icon: '/product-icons/datapick.svg',
        url: 'https://datapick.toolooz.com/',
      },
      {
        id: 'subU',
        icon: '/product-icons/subu.svg',
        url: 'https://subu.toolooz.com/',
      },
      {
        id: 'castLoomPro',
        icon: '/product-icons/castloompro.svg',
        url: 'https://castloompro.toolooz.com/',
      },
    ],
    []
  );
  const [promoProduct] = useState(() => {
    const index = Math.floor(Math.random() * promoProducts.length);
    return promoProducts[index];
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await getFile(file);
      if (onStartEditing) onStartEditing();
    }
  };

  const handleDemoClick = async () => {
    await getFile(demoPng, 'dataURL');
    if (onStartEditing) onStartEditing();
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim()) {
      messageApi.error('请输入图片URL');
      return;
    }

    console.log('开始加载图片URL:', imageUrl);
    messageApi.loading('正在加载图片，请稍候...');

    try {
      // 使用安全的图片加载函数
      const blob = await safeLoadImageFromUrl(imageUrl);
      console.log('获取到blob数据:', blob.type, blob.size);
      
      await getFile(blob);
      console.log('图片加载成功');
      messageApi.success('图片加载成功！');
      if (onStartEditing) onStartEditing();
    } catch (error) {
      console.error('图片加载错误:', error);
      messageApi.error(`无法加载图片: ${error.message || '请检查URL是否正确或图片是否允许跨域访问'}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title={t('landing.seo.title')}
        description={t('landing.seo.description')}
        keywords={t('landing.seo.keywords')}
        canonicalUrl="https://snapbeautify.pixzens.com/"
      />
      {contextHolder}
      
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <a href="https://snapbeautify.pixzens.com" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt={t('about.logoText')} width="32" height="32" className="mr-2" />
                {t('about.logoText')}
              </a>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="font-medium text-purple-600 dark:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                {tCommon('home')}
              </Link>
              <Link to="/video-capture" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Video" size={16} className="mr-1" />
                {tCommon('videoCapture')}
              </Link>
              <Link to="/quick-start" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                {tCommon('quickStart')}
              </Link>
              <Link to="/about" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                {tCommon('about')}
              </Link>
              <LanguageSwitcher />
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title={tCommon('navigation')}
          placement="right"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              {tCommon('home')}
            </Link>
            <Link 
              to="/video-capture" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Video" size={16} className="mr-1" />
              {tCommon('videoCapture')}
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              {tCommon('quickStart')}
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              {tCommon('about')}
            </Link>
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <LanguageSwitcher />
            </div>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* 英雄区域 */}
        <div className="text-center mb-16 mt-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 dark:text-white mb-4">
            {t('landing.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('landing.subtitle')}
          </p>
          
          {/* 上传图片卡片 */}
          <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-12">
            <div className="md:flex">
              <div className="md:flex-1 p-8">
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 p-6 rounded-lg mb-6">
                  <div className="text-center">
                    <SafeIcon icon="ImagePlus" className="mx-auto text-purple-600 dark:text-purple-400" size={48} />
                    <h3 className="mt-4 text-xl font-semibold text-gray-800 dark:text-white">{t('landing.startBeautify')}</h3>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">{t('landing.uploadDescription')}</p>
                  </div>
                </div>
                
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="file-upload" className="flex items-center justify-center w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg cursor-pointer transition duration-200">
                      <SafeIcon icon="Upload" size={20} className="mr-2" />
                      {t('landing.uploadImage')}
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept={supportImg.join(',')}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={t('landing.pasteUrl')}
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      prefix={<SafeIcon icon="Link" size={16} className="text-gray-400" />}
                      className="flex-grow"
                    />
                    <Button
                      type="primary"
                      onClick={handleUrlSubmit}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {t('landing.get')}
                    </Button>
                  </div>
                  
                  <Button
                    type="default"
                    onClick={handleDemoClick}
                    className="border border-dashed border-gray-300 hover:border-purple-500 hover:text-purple-600 dark:border-gray-600 dark:hover:border-purple-400 dark:hover:text-purple-400"
                  >
                    <div className="flex items-center">
                      <div className="w-5 h-5 mr-2 overflow-hidden rounded-sm">
                        <img src={demoPng} alt={t('landing.tryDemo')} className="w-full h-full object-cover" />
                      </div>
                      <span>{t('landing.tryDemo')}</span>
                    </div>
                  </Button>
                </div>
              </div>
              
              <div className="md:flex-1 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-8 flex items-center justify-center">
                <div className="relative w-full max-w-md">
                  <img src="/device-showcase.svg" alt="图片展示" className="w-full h-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <section
          aria-label={t('landing.productPromo.ariaLabel')}
          className="mx-auto my-10 w-full max-w-4xl"
        >
          <div className="rounded-2xl border border-purple-200/70 dark:border-purple-700/50 bg-white/90 dark:bg-gray-800/80 shadow-lg p-5 md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <a
                  href={promoProduct.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('landing.productPromo.iconLinkLabel', {
                    product: t(`landing.productPromo.products.${promoProduct.id}.name`),
                  })}
                  className="shrink-0"
                >
                  <img
                    src={promoProduct.icon}
                    alt={t(`landing.productPromo.products.${promoProduct.id}.name`)}
                    className="w-12 h-12 rounded-xl bg-white"
                    loading="lazy"
                  />
                </a>
                <div>
                  <p className="text-sm text-purple-600 dark:text-purple-300 font-medium mb-1">
                    {t('landing.productPromo.title')}
                  </p>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 dark:text-white">
                    {t(`landing.productPromo.products.${promoProduct.id}.name`)}
                  </h3>
                  <p className="mt-1 text-gray-600 dark:text-gray-300">
                    {t(`landing.productPromo.products.${promoProduct.id}.description`)}
                  </p>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {t('landing.productPromo.tip')}
                  </p>
                </div>
              </div>
              <div className="md:text-right">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  {t('landing.productPromo.subtitle')}
                </p>
                <a
                  href={promoProduct.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 transition-colors"
                >
                  {t(`landing.productPromo.products.${promoProduct.id}.cta`)}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 功能介绍区域 */}
        <div id="guide" className="py-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-12">
            <SafeIcon icon="Sparkles" className="inline-block mr-2 text-yellow-500" size={28} />
            {t('landing.powerfulFeatures')}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 功能卡片 1 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <SafeIcon icon="Layers" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.smartLayout.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.smartLayout.description')}
              </p>
            </Card>

            {/* 视频截图功能卡片 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <SafeIcon icon="Video" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.videoCapture.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.videoCapture.description')}
                <Link to="/video-capture" className="block mt-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300">
                  {t('landing.features.videoCapture.tryNow')}
                </Link>
              </p>
            </Card>

            {/* 功能卡片 2 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <SafeIcon icon="Palette" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.beautifulBackground.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.beautifulBackground.description')}
              </p>
            </Card>

            {/* 功能卡片 3 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400">
                  <SafeIcon icon="Edit3" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.annotationTools.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.annotationTools.description')}
              </p>
            </Card>

            {/* 功能卡片 4 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <SafeIcon icon="Crop" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.cropAndAdjust.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.cropAndAdjust.description')}
              </p>
            </Card>

            {/* 功能卡片 5 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                  <SafeIcon icon="Download" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.multiFormatExport.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.multiFormatExport.description')}
              </p>
            </Card>

            {/* 功能卡片 6 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-red-50 to-red-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  <SafeIcon icon="Zap" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.simpleAndFast.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.simpleAndFast.description')}
              </p>
            </Card>
            
            {/* 功能卡片 7 - 新增 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <SafeIcon icon="Shield" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.safeAndReliable.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.safeAndReliable.description')}
              </p>
            </Card>

            {/* 功能卡片 8 - 新增 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
                  <SafeIcon icon="Globe" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">{t('landing.features.freeOnline.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t('landing.features.freeOnline.description')}
              </p>
            </Card>
          </div>
        </div>

        {/* 关于我们区域 */}
        <div id="about" className="py-16">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
                <SafeIcon icon="Users" className="mr-2 text-blue-600 dark:text-blue-400" size={28} />
                {t('landing.aboutSection.title')}
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t('landing.aboutSection.description1')}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                {t('landing.aboutSection.description2')}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="Info" size={18} className="mr-2 text-blue-400" />
                {t('landing.footer.aboutUs')}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('landing.footer.aboutDescription')}
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="FileText" size={18} className="mr-2 text-purple-400" />
                {t('landing.footer.terms')}
              </h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li><a href="/privacy" className="hover:text-white transition-colors">{t('landing.footer.privacy')}</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">{t('landing.footer.termsOfService')}</a></li>
                <li><a href="/copyright" className="hover:text-white transition-colors">{t('landing.footer.copyright')}</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="BookOpen" size={18} className="mr-2 text-green-400" />
                {t('landing.footer.quickStart')}
              </h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li><a href="/quick-start" className="hover:text-white transition-colors">{t('landing.footer.quickStart')}</a></li>
                <li><a href="/faq" className="hover:text-white transition-colors">{t('landing.footer.faq')}</a></li>
                <li><a href="/features" className="hover:text-white transition-colors">{t('landing.footer.features')}</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="MessageSquare" size={18} className="mr-2 text-yellow-400" />
                {t('landing.footer.contactUs')}
              </h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li className="flex items-center">
                  <SafeIcon icon="Mail" size={16} className="mr-2 text-blue-400" />
                  <a href="mailto:support@pixzens.com" className="hover:text-white transition-colors">
                    {t('landing.footer.email')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>© {new Date().getFullYear()} SnapBeautify - {t('landing.footer.copyrightText')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 